import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Card, Flex, Tooltip, Typography } from "antd";
import { IconTone, ICON_TONES } from "shared/theme/color";
import { colors } from "shared/theme/color";
import { DashboardMetric } from "shared/types";
import { textPx, wholeScreenPx } from "shared/constants/zoom";

interface DashboardMetricCardProps extends DashboardMetric {
  title: string;
  icon: React.ReactNode;
  info?: string;
  /** Icon chip tint. The dashboard KPI row keeps all three navy: the cards are siblings, and colour there is reserved for the trend pill. */
  tone?: IconTone;
}

// Month-over-month change as a small tinted pill beside this month's figure:
// green up, red down, neutral flat. Colour here carries information, it
// isn't decoration.
//
// Only computed from two real NUMBERS. The financial cards send formatted
// strings ("Rp 5.698.000"), and parsing those is exactly how a wrong figure
// would slip in (Number("1.234") is 1.234, Number("") is 0), so a string
// never gets a pill. Also hidden when last month is zero: every percentage
// against zero is meaningless. Rounded half away from zero so -92.5 and
// +92.5 both read as 93 (Math.round alone would give -92 and 93).
export const percentChange = (
  current: number | string,
  previous: number | string,
): number | null => {
  if (typeof current !== "number" || typeof previous !== "number") return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;
  const raw = ((current - previous) / previous) * 100;
  const rounded = Math.sign(raw) * Math.round(Math.abs(raw));
  return rounded === 0 ? 0 : rounded; // normalise -0
};

const TrendPill = ({
  current,
  previous,
}: {
  current: number | string;
  previous: number | string;
}) => {
  const pct = percentChange(current, previous);
  if (pct === null) return null;
  const up = pct > 0;
  const flat = pct === 0;
  const palette = flat
    ? { bg: colors.bgSoft, fg: colors.ink2 }
    : up
      ? { bg: colors.successTint, fg: colors.successDark }
      : { bg: colors.errorTint, fg: colors.error };
  return (
    <span
      className="tnum"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        height: 20,
        padding: "0 7px",
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        fontSize: textPx(11.5),
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {!flat && (up ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
      {Math.abs(pct)}%
    </span>
  );
};

// One side of the current-vs-previous comparison. Both sides share font-size
// and line-height so the figures align on a baseline and the period labels land
// on the same row; `dimmed` mutes the secondary figure to ink-2, and `divider`
// adds the hairline separator + indent used on the right column.
const MetricFigure = ({
  value,
  label,
  dimmed = false,
  divider = false,
  aside,
}: {
  value: React.ReactNode;
  label: React.ReactNode;
  dimmed?: boolean;
  divider?: boolean;
  /** Rendered beside the figure (the trend pill on the current column). */
  aside?: React.ReactNode;
}) => (
  <div
    style={{
      minWidth: 0,
      ...(divider
        ? { borderLeft: `1px solid ${colors.line}`, paddingLeft: 12 }
        : {}),
    }}
  >
    <Flex align="center" gap={8} wrap="wrap">
      <Typography.Text
        className="display tnum"
        strong
        style={{
          fontSize: textPx(26),
          display: "block",
          lineHeight: 1.2,
          color: dimmed ? colors.ink2 : undefined,
        }}
      >
        {value}
      </Typography.Text>
      {aside}
    </Flex>
    <Typography.Text
      style={{
        fontSize: textPx(11),
        color: colors.ink3,
        marginTop: 4,
        display: "block",
      }}
    >
      {label}
    </Typography.Text>
  </div>
);

const DashboardMetricCard = ({
  title,
  icon,
  current,
  previous,
  current_label,
  previous_label,
  info,
  tone = "neutral",
}: DashboardMetricCardProps) => {
  const chip = ICON_TONES[tone];
  return (
    <Card
      style={{
        borderRadius: 16,
        border: `1px solid ${colors.line}`,
        boxShadow: "none",
      }}
      styles={{ body: { padding: "18px 20px" } }}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 14 }}>
        <Flex align="center" gap={5}>
          <Typography.Text
            style={{ fontSize: textPx(12), lineHeight: `${textPx(16)}px`, color: colors.ink2 }}
          >
            {title}
          </Typography.Text>
          {info && (
            <Tooltip title={info}>
              {/* Every size here lands on a whole pixel on screen after the
                  body zoom: a fractional box made Safari round the glyph's
                  viewport down and clip the bottom of the circle. The glyph is centred in its own box, with no
                  offset, and the svg is a block so no line box can cut it. */}
              <span
                className="dashboard-metric-info"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: wholeScreenPx(textPx(16)),
                  height: wholeScreenPx(textPx(16)),
                  flexShrink: 0,
                  color: colors.ink3,
                  cursor: "help",
                  lineHeight: 1,
                }}
              >
                <InfoCircleOutlined style={{ fontSize: wholeScreenPx(textPx(12)) }} />
              </span>
            </Tooltip>
          )}
        </Flex>
        {/* Icon chip, tinted per card (same TONES as the Pengaturan rows) */}
        <div
          style={{
            height: 38,
            width: 38,
            borderRadius: 10,
            background: chip.bg,
            color: chip.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: textPx(16),
          }}
        >
          {icon}
        </div>
      </Flex>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          // Top-align both columns so the two figures share a baseline and the
          // two period labels land on the same row.
          alignItems: "start",
        }}
      >
        <MetricFigure
          value={current}
          label={current_label}
          aside={<TrendPill current={current} previous={previous} />}
        />
        <MetricFigure value={previous} label={previous_label} dimmed divider />
      </div>
    </Card>
  );
};

export default DashboardMetricCard;
